import { LEAGUE_DEFS, buildLeagueField, currentRules, statsToParams } from '../race/params';
import type { HorseParams, RaceOpponent, RaceSetup } from '../race/types';
import { DerbyRngV1 } from '../rng';
import {
  DERBY_LEAGUE,
  classAdjustedStats,
  eventDay,
  eventLeague,
  goingOf,
  raceClassOf,
  type Going,
  type RaceClassDef,
} from './economy';
import { coatPhenotype } from './genetics';
import { expressedTraits, applyRaceTraits } from './traits';
import { ratingOf } from './rivals';
import type { RanchSim } from './sim';
import type { Horse } from './types';
import { raceSeed } from './view';

/** Distance class and weather act on the derived physics — the same
 * multipliers for every horse in the field. */
function applyConditions(
  params: HorseParams,
  raceClass: RaceClassDef,
  going: Going,
  mudlark: boolean,
): HorseParams {
  const out = { ...params };
  out.tapCost *= raceClass.drainMul;
  if (going.going === 'good') {
    out.tapImpulse *= 0.98;
    out.vMax *= 0.98;
  } else if (going.going === 'soft' && !mudlark) {
    out.tapImpulse *= 0.92;
    out.vMax *= 0.92;
    out.tapCost *= 1.15;
  }
  if (going.surface === 'turf') out.gateAccel *= 1.05;
  return out;
}

function rivalOpponent(
  horse: Horse,
  info: RanchSim['state']['rivals'][number],
  raceClass: RaceClassDef,
  going: Going,
): RaceOpponent {
  const traits = expressedTraits(horse.genotype.traits);
  const effectiveStats = {
    speed: Math.max(horse.stats.speed, info.statFloor.speed),
    stamina: Math.max(horse.stats.stamina, info.statFloor.stamina),
    accel: Math.max(horse.stats.accel, info.statFloor.accel),
    heart: Math.max(horse.stats.heart, info.statFloor.heart),
  };
  let params = applyRaceTraits(statsToParams(classAdjustedStats(effectiveStats, raceClass.id)), traits);
  params = applyConditions(params, raceClass, going, traits.includes('mudlark'));
  return { params, policy: info.style, name: horse.name, coatId: coatPhenotype(horse.genotype.coat) };
}

/** The field: named rivals near the player's class — or the Derby's
 * all-star gathering of every league's best. */
function buildField(
  sim: RanchSim,
  league: number,
  raceClass: RaceClassDef,
  going: Going,
  seed: number,
  playerHorse: Horse,
): RaceOpponent[] {
  const state = sim.state;
  const entries = state.rivals
    .map((info) => ({ info, horse: sim.horseById(info.horseId) }))
    .filter((entry): entry is { info: (typeof state.rivals)[number]; horse: Horse } =>
      entry.horse !== null && !entry.horse.retired,
    );

  let picked: { info: (typeof state.rivals)[number]; horse: Horse }[];
  if (league === DERBY_LEAGUE) {
    // The best of each league, then the next best anywhere.
    const byLeague = [0, 1, 2].map((tier) =>
      entries
        .filter((entry) => entry.info.league === tier)
        .sort((a, b) => ratingOf(b.horse.stats) - ratingOf(a.horse.stats)),
    );
    const champions = byLeague.map((tier) => tier[0]).filter((entry) => entry !== undefined);
    const rest = entries
      .filter((entry) => !champions.includes(entry))
      .sort((a, b) => ratingOf(b.horse.stats) - ratingOf(a.horse.stats));
    picked = [...champions, ...rest].slice(0, 5);
  } else {
    const playerRating = ratingOf(playerHorse.stats);
    picked = entries
      .filter((entry) => entry.info.league === league)
      .sort(
        (a, b) =>
          Math.abs(ratingOf(a.horse.stats) - playerRating) -
          Math.abs(ratingOf(b.horse.stats) - playerRating),
      )
      .slice(0, 5);
  }

  const field = picked.map((entry) => rivalOpponent(entry.horse, entry.info, raceClass, going));
  if (field.length < 5) {
    // Shouldn't happen with a full roster, but never under-fill a gate.
    const filler = buildLeagueField(LEAGUE_DEFS[Math.min(league, 2)]!, new DerbyRngV1(seed).fork('filler'));
    for (const opponent of filler) {
      if (field.length >= 5) break;
      opponent.params = applyConditions(opponent.params, raceClass, going, false);
      field.push(opponent);
    }
  }
  return field;
}

/** Build the race a ranch event resolves to. One code path for the live
 * RaceScene and the season harness, so they can never drift apart.
 * Traits, distance class, and the day's going all land here. */
export function raceSetupFor(sim: RanchSim, forEventId: number): RaceSetup {
  const league = eventLeague(forEventId);
  const def = LEAGUE_DEFS[league];
  if (!def) throw new Error(`Bad event ${forEventId}`);
  const entry = sim.entryForEvent(forEventId);
  const horse = entry ? sim.horseById(entry.horseId) : sim.featured();
  if (!horse) throw new Error('No featured horse to race.');
  const day = eventDay(forEventId);
  const raceClass = raceClassOf(day, league);
  const going = goingOf(sim.state.meta.seed, day, league);
  const seed = raceSeed(sim, forEventId);

  const traits = sim.traitsOf(horse);
  let player = applyRaceTraits(
    statsToParams(classAdjustedStats({ ...horse.stats }, raceClass.id)),
    traits,
  );
  player = applyConditions(player, raceClass, going, traits.includes('mudlark'));

  return {
    seed,
    distanceM: raceClass.distanceM,
    playerName: horse.name,
    player,
    opponents: buildField(sim, league, raceClass, going, seed, horse),
    exec: def.exec,
    rules: currentRules(),
  };
}
